import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { Kind, Queue, Round, Stage } from '../../lib/approvals'
import { KIND_LABEL, useApprovals } from '../../lib/approvals'
import { canEdit } from '../../lib/nav'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { useAuthedSession } from '../../lib/SessionContext'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { SectionHead, ViewerNote } from '../offsite/parts'
import { FormDrawer, toneClass } from './FormDrawer'

/* ============================================================================
   อนุมัติคำขอ — ใบลา / ใบปรับปรุงเวลา (เฟส 1 · OT อยู่เฟส 2)
   API: backend/src/routes/adminApprovals.js · lib/approvals.js

   ── หน้านี้ทำได้เฉพาะ "ชั้น HR" ────────────────────────────────────────────
   สายอนุมัติจริงคือ หัวหน้าชั้น 1 → หัวหน้าชั้น 2 → HR
   ใบที่ยังค้างที่หัวหน้า **แสดงให้เห็นว่าค้างที่ใคร** แต่กดแทนไม่ได้
   (วัดจากรอบ 21/07–20/08/2026: ค้างหัวหน้า 39 ใบ · ถึงมือ HR แล้ว 1 ใบ)
   → เปิดมาแล้วคิว "รอ HR" ว่าง ไม่ใช่หน้าพัง — ดูตัวเลขบนชิปสถานะ

   ⚠️ ตัวกรองเป็น **รอบ 21 → 20** ไม่ใช่เดือนปฏิทิน อ่านจากตาราง HR_OT_PERIOD
      (ใส่ล่วงหน้าถึงปี 2030) ไม่ได้คิดวันที่ 21 เองในโค้ด
   ============================================================================ */

const KINDS: Kind[] = ['leave', 'adjust']

/** ลำดับชิปสถานะ — เรียงตามที่คนทำงานสนใจ ไม่ใช่ตามตัวอักษร */
const STAGE_ORDER: Stage[] = ['wait_hr', 'wait_chain', 'draft', 'done', 'reject', 'cancel', 'other']

const nf = new Intl.NumberFormat('th-TH')

/** 'YYYY-MM-DD' → 'DD/MM' (ในตัวเลือกรอบ ปีซ้ำกันอยู่ใน roundId แล้ว) */
const shortDay = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

const roundLabel = (r: Round) =>
  `${r.roundId} (${shortDay(r.from)}–${shortDay(r.to)})${r.closed ? ' · ปิดรอบแล้ว' : ''}`

export default function Approvals() {
  const api = useApprovals()
  const session = useAuthedSession()
  const writable = canEdit(session)

  /* เมนู "ประวัติคำขอ" ใช้หน้าเดียวกันนี้ ต่างแค่เปิดมาที่สถานะ "อนุมัติแล้ว"
     หน้านี้กรองได้ทุกสถานะอยู่แล้ว การทำหน้าประวัติแยกคือก๊อปทั้งหน้ามาเปลี่ยน
     ค่าเริ่มต้น แล้วต้องตามแก้สองที่ทุกครั้งที่ตารางเปลี่ยน

     ⚠️ ดูจาก pathname ไม่ใช่ query string — canAccess() ใน lib/nav.ts เทียบ
        path แบบตรงตัว (item.path === pathname) ถ้าใส่ ?stage= ในเมนู เมนูนั้น
        จะหาไม่เจอใน NAV_LEAVES แล้วกลายเป็นหน้าที่ทุกคนเข้าได้ */
  const isHistory = useLocation().pathname.endsWith('/history')
  const initialStage: Stage = isHistory ? 'done' : 'wait_hr'

  const [kind, setKind] = useState<Kind>('leave')
  /* '' = ให้ backend เลือกรอบที่ครอบวันนี้ — ไม่คิดรอบเองฝั่งหน้าจอ เพราะนาฬิกา
     เครื่องผู้ใช้กับวันตัดรอบใน DB ไม่จำเป็นต้องตรงกัน */
  const [round, setRound] = useState('')
  const [rounds, setRounds] = useState<Round[]>([])
  /* ค่าเริ่มต้น = คิวที่ HR ทำได้จริง ตรงกับงานที่หน้านี้ถูกสั่งให้ทำ
     ("ดู pending ค้าง hr อนุมัติ") สถานะอื่นกดชิปดูได้
     · เข้ามาจากเมนู "ประวัติ" จะมาพร้อม ?stage=done */
  const [stage, setStage] = useState<Stage | ''>(initialStage)
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const sort = useSort([{ key: 'firstDay', dir: 'asc' }], () => setPage(1))

  const [data, setData] = useState<Queue | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [openRow, setOpenRow] = useState<string | null>(null)

  useEffect(() => {
    api
      .rounds()
      .then((res) => setRounds(res.rounds))
      // รอบโหลดไม่ได้ = dropdown ว่าง แต่คิวยังใช้ได้ (backend เลือกรอบปัจจุบันให้)
      .catch(() => setRounds([]))
  }, [api])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await api.queue({ kind, round, stage, q, page, pageSize, sort: sort.param }))
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
    // ⚠️ sort.param ต้องอยู่ใน deps — เคยตกมาแล้วทั้งระบบ แล้วกดหัวตารางไม่โหลดใหม่
  }, [api, kind, round, stage, q, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  const forms = data?.forms ?? []
  const counts = data?.counts ?? []
  const current = data?.round ?? null
  const waitHr = counts.find((c) => c.stage === 'wait_hr')?.forms ?? 0
  const waitChain = counts.find((c) => c.stage === 'wait_chain')?.forms ?? 0
  const allInRound = counts.reduce((sum, c) => sum + c.forms, 0)

  return (
    <>
      <h1>{isHistory ? 'ประวัติคำขอ' : 'อนุมัติคำขอ'}</h1>
      <p className="meta">
        {isHistory ? (
          <>
            ใบลา / ใบปรับปรุงเวลา ที่ตัดสินไปแล้ว · กรองตาม
            <strong>รอบวันที่ 21 ถึง 20</strong> ของเดือนถัดไป · กดชิปด้านล่างเพื่อดูสถานะอื่น
          </>
        ) : (
          <>
            ใบที่ผ่านหัวหน้ามาครบแล้วและรอ HR ปิดท้าย · กรองตาม
            <strong>รอบวันที่ 21 ถึง 20</strong> ของเดือนถัดไป ·
            แก้ข้อมูลที่พนักงานกรอกผิดได้ในใบเดิม ไม่ต้องตีกลับให้ยื่นใหม่
          </>
        )}
      </p>

      {!writable && <ViewerNote />}
      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <section className="card">
        <SectionHead
          title={KIND_LABEL[kind]}
          meta={
            current
              ? `รอบ ${current.roundId} · ${current.from} ถึง ${current.to}${current.closed ? ' (ปิดรอบแล้ว)' : ''}`
              : undefined
          }
          onReload={() => void load()}
          loading={loading}
        />

        {/* แท็บประเภท — คนละคิวคนละเรื่อง (ลา = ตัดสิทธิ์ · ปรับปรุงเวลา = แก้เวลาเข้าออก) */}
        <div className="tabs" role="tablist">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              className={kind === k ? 'tab tab--on' : 'tab'}
              onClick={() => {
                setKind(k)
                setPage(1)
                setOpenRow(null)
              }}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
          <span className="tab-note">OT อยู่เฟสถัดไป</span>
        </div>

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(term.trim())
          }}
        >
          <label>
            รอบ
            <select
              value={round}
              onChange={(e) => {
                setPage(1)
                setRound(e.target.value)
              }}
            >
              <option value="">รอบปัจจุบัน</option>
              {rounds.map((r) => (
                <option key={r.roundId} value={r.roundId}>
                  {roundLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
        </form>

        {/* ชิปสถานะ = ตัวกรอง + ตัวเลขของ **ทั้งรอบ** (ไม่ใช่ของหน้าที่โหลดมา)
            ต้องเห็นทุกสถานะพร้อมกัน ไม่งั้นคิวที่ว่างจะดูเหมือนหน้าพัง */}
        <div className="chips">
          <button
            type="button"
            className={stage === '' ? 'chipbtn chipbtn--on' : 'chipbtn'}
            onClick={() => {
              setPage(1)
              setStage('')
            }}
          >
            ทั้งรอบ {nf.format(allInRound)}
          </button>
          {STAGE_ORDER.map((key) => {
            const c = counts.find((x) => x.stage === key)
            if (!c || c.forms === 0) return null
            return (
              <button
                key={key}
                type="button"
                className={stage === key ? 'chipbtn chipbtn--on' : 'chipbtn'}
                onClick={() => {
                  setPage(1)
                  setStage(key)
                }}
              >
                <span className={toneClass(c.tone)}>{c.text}</span> {nf.format(c.forms)}
              </button>
            )
          })}
        </div>

        {/* คิว "รอ HR" ว่างแต่มีใบค้างที่หัวหน้า — ชี้ให้ไปกดชิป "รอหัวหน้า"
            (ตอนนี้กดอนุมัติแทนชั้นหัวหน้าได้แล้ว ถ้าผู้ใช้เป็นหัวหน้าชั้นนั้น) */}
        {stage === 'wait_hr' && waitHr === 0 && waitChain > 0 && !loading && (
          <p className="warn">
            รอบนี้ยังไม่มีใบที่ถึงมือ HR — แต่มี <strong>{nf.format(waitChain)} ใบ</strong>
            ค้างอยู่ที่หัวหน้า กดชิป "รอหัวหน้า" เพื่อดู (หัวหน้าชั้นที่ใบรออยู่กดอนุมัติได้)
          </p>
        )}

        <SortBar
          sort={sort.sort}
          labels={{
            firstDay: 'วันที่',
            empCode: 'รหัส',
            empName: 'ชื่อ',
            leaveType: 'ประเภท',
            hours: 'ชั่วโมง',
            dayRows: 'จำนวนแถว',
            createdAt: 'วันที่ยื่น',
            stage: 'สถานะ',
          }}
          onClear={() => sort.setSort([])}
        />

        {data === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : forms.length === 0 ? (
          <p className="meta">ไม่มีใบตามเงื่อนไขนี้</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh label="วันที่" sortKey="firstDay" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <SortTh label="พนักงาน" sortKey="empName" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ประเภท" sortKey="leaveType" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ชั่วโมง" sortKey="hours" sort={sort.sort} onToggle={sort.toggle} className="num" />
                  <SortTh label="สถานะ" sortKey="stage" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ยื่นเมื่อ" sortKey="createdAt" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <th />
                </tr>
              </thead>
              <tbody>
                {forms.map((f) => (
                  <tr key={f.rowId}>
                    <td className="nowrap">
                      {f.firstDay === f.lastDay ? f.firstDay : `${f.firstDay} → ${f.lastDay}`}
                      {f.dayRows > 1 && <div className="meta">{f.dayRows} แถวในใบเดียว</div>}
                    </td>
                    <td>
                      <strong>{f.empName ?? '-'}</strong>
                      <div className="meta">{f.empCode}</div>
                    </td>
                    <td>
                      {f.typeDesc}
                      {f.mixedTypes && <div className="bad">ปนหลายประเภทในใบเดียว</div>}
                    </td>
                    <td className="num">{f.hours}</td>
                    <td className="nowrap">
                      <span className={toneClass(f.stageTone)}>{f.stageText}</span>
                      {f.waitingAt && (
                        <div className="meta">รอ {f.waitingAt.name ?? f.waitingAt.by}</div>
                      )}
                    </td>
                    <td className="nowrap">{f.createdAt ? f.createdAt.slice(0, 10) : '-'}</td>
                    <td className="num">
                      <button type="button" className="ghost" onClick={() => setOpenRow(f.rowId)}>
                        {/* viewer เปิดดูได้ แค่ไม่มีปุ่มบันทึกข้างใน */}
                        {f.actionable && writable ? 'ตรวจ / อนุมัติ' : 'ดูรายละเอียด'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pager
          page={data?.page ?? page}
          pageSize={data?.pageSize ?? pageSize}
          total={data?.total ?? 0}
          loading={loading}
          onPage={setPage}
          unit="ใบ"
          onPageSize={(n) => {
            setPageSize(n)
            setPage(1)
          }}
        />
      </section>

      {openRow && (
        <FormDrawer
          rowId={openRow}
          writable={writable}
          onClose={() => setOpenRow(null)}
          onDone={(message) => {
            setOpenRow(null)
            setNotice(message)
            void load()
          }}
        />
      )}
    </>
  )
}
